import React, { useState, useEffect, useMemo } from 'react';
import { useLMS } from '../../context/LMSContext';
import { supabase, db } from '../../lib/supabase';
import StudentNotesPanel from './StudentNotesPanel';
import EligibilityTracker from './EligibilityTracker';

const StudentProgress = () => {
  const {
    cohorts,
    modules,
    selectedCohortId,
    setSelectedCohortId,
    getUsersForCohort,
    getCohortById,
    cohortItemOverrides
  } = useLMS();

  const [studentsProgress, setStudentsProgress] = useState({});
  const [studentsPercentiles, setStudentsPercentiles] = useState({});
  const [studentsAttendance, setStudentsAttendance] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [expandedStudents, setExpandedStudents] = useState({});
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [notesStudent, setNotesStudent] = useState(null);
  const [eligibilityData, setEligibilityData] = useState({ sessions: [], records: [], submissions: [] });
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [eligibilityModalStudent, setEligibilityModalStudent] = useState(null);

  // Get students for selected cohort
  const students = selectedCohortId ? getUsersForCohort(selectedCohortId) : [];
  const selectedCohort = cohorts.find(c => c.id === selectedCohortId);

  // Load progress for all students in the cohort
  useEffect(() => {
    if (!selectedCohortId || students.length === 0) {
      setStudentsProgress({});
      setStudentsPercentiles({});
      setStudentsAttendance({});
      return;
    }

    const loadAllStudentProgress = async () => {
      setIsLoading(true);
      try {
        // Get progress for all students in the cohort
        const studentIds = students.map(s => s.id);
        
        const [progressRes, percentilesRes] = await Promise.all([
          supabase
            .from('student_progress')
            .select('*')
            .in('user_id', studentIds),
          db.getCohortPercentiles(selectedCohortId)
        ]);

        if (progressRes.error) {
          console.error('Error loading student progress:', progressRes.error);
        }

        // Organize progress by student
        const progressByStudent = {};
        studentIds.forEach(id => {
          progressByStudent[id] = {};
        });

        if (progressRes.data) {
          progressRes.data.forEach(p => {
            if (!progressByStudent[p.user_id]) {
              progressByStudent[p.user_id] = {};
            }
            progressByStudent[p.user_id][p.module_item_id] = p;
          });
        }

        setStudentsProgress(progressByStudent);

        // Organize percentiles by student
        const percentilesByStudent = {};
        if (percentilesRes.data) {
          percentilesRes.data.forEach(p => {
            if (!percentilesByStudent[p.student_id]) {
              percentilesByStudent[p.student_id] = [];
            }
            percentilesByStudent[p.student_id].push(p);
          });
        }
        setStudentsPercentiles(percentilesByStudent);

        // Load attendance summaries for all students
        const attendanceByStudent = {};
        for (const sid of studentIds) {
          try {
            const { data: summary } = await db.getStudentAttendanceSummary(sid, selectedCohortId);
            if (summary) {
              attendanceByStudent[sid] = summary;
            }
          } catch (err) {
            // silently skip if attendance tables don't exist yet
          }
        }
        setStudentsAttendance(attendanceByStudent);

        // Load eligibility data (attendance records + submissions)
        try {
          const [attRes, subRes] = await Promise.all([
            db.getCohortAttendanceRecords(selectedCohortId),
            db.getCohortSubmissionsForEligibility(selectedCohortId)
          ]);
          setEligibilityData({
            sessions: attRes.data?.sessions || [],
            records: attRes.data?.records || [],
            submissions: subRes.data || []
          });
        } catch (err) {
          // silently skip if eligibility data can't be loaded
        }

      } catch (err) {
        console.error('Error loading progress:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllStudentProgress();
  }, [selectedCohortId, students.length]);

  // Calculate progress stats for a student
  const getStudentStats = (studentId) => {
    const studentProgress = studentsProgress[studentId] || {};
    let totalItems = 0;
    let completedItems = 0;
    const moduleStats = {};

    modules.forEach(module => {
      const weekNumber = module.weekNumber || module.week_number;
      const items = module.items || [];
      const moduleCompleted = items.filter(item => 
        studentProgress[item.id]?.completed === true
      ).length;

      moduleStats[weekNumber] = {
        total: items.length,
        completed: moduleCompleted,
        percentage: items.length > 0 ? Math.round((moduleCompleted / items.length) * 100) : 0
      };

      totalItems += items.length;
      completedItems += moduleCompleted;
    });

    return {
      totalItems,
      completedItems,
      overallPercentage: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0,
      moduleStats
    };
  };

  // Get latest UWorld percentile for a student
  const getLatestPercentile = (studentId) => {
    const percentiles = studentsPercentiles[studentId] || [];
    if (percentiles.length === 0) return null;
    
    // Get the most recent week's percentile (UWorld preferred)
    const uworldPercentiles = percentiles.filter(p => p.source === 'uworld');
    const sorted = (uworldPercentiles.length > 0 ? uworldPercentiles : percentiles)
      .sort((a, b) => b.week_number - a.week_number);
    
    return sorted[0] || null;
  };

  // Get attendance summary for a student
  const getAttendanceRate = (studentId) => {
    const attendance = studentsAttendance[studentId];
    if (!attendance || attendance.total === 0) return null;
    const rate = Math.round(((attendance.present + attendance.late) / attendance.total) * 100);
    return { ...attendance, rate };
  };

  // Open notes panel for a student
  const openNotesPanel = (student) => {
    setNotesStudent(student);
    setShowNotesPanel(true);
  };

  // Toggle student expansion
  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => ({
      ...prev,
      [studentId]: !prev[studentId]
    }));
  };

  // Open detailed view for a student
  const openStudentDetail = (student) => {
    setSelectedStudent(student);
  };

  // Compute eligibility for each student (mirrors EligibilityTracker logic)
  const cohort = getCohortById(selectedCohortId);

  const assignmentsWithDueDates = useMemo(() => {
    if (!cohort) return [];
    const overrides = cohortItemOverrides[selectedCohortId] || {};
    const startDateStr = cohort.startDate || cohort.start_date;
    if (!startDateStr) return [];

    const [sY, sM, sD] = startDateStr.split('-').map(Number);
    const cohortStart = new Date(sY, sM - 1, sD);
    const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

    const calculateDueDate = (weekNumber, dayOfWeek) => {
      const targetDayIndex = dayMap[dayOfWeek] ?? null;
      if (targetDayIndex === null) return null;
      const cohortStartDayIndex = cohortStart.getDay();
      let daysToTargetDay = (targetDayIndex - cohortStartDayIndex + 7) % 7;
      const weekOffset = ((weekNumber || 1) - 1) * 7;
      const eventDate = new Date(cohortStart);
      eventDate.setDate(eventDate.getDate() + weekOffset + daysToTargetDay);
      eventDate.setHours(23, 59, 59, 999);
      return eventDate;
    };

    const items = [];
    modules.forEach(module => {
      const weekNum = module.weekNumber || module.week_number;
      (module.items || []).forEach(item => {
        if (!['assignment', 'quiz', 'exam', 'workbook'].includes(item.type)) return;
        if (!item.countsForEligibility && !item.counts_for_eligibility) return;
        const override = overrides[item.id];
        const explicitDueDate = override?.due_date || override?.dueDate || null;
        if (explicitDueDate) {
          items.push({ id: item.id, title: item.title, weekNumber: weekNum, dueDate: new Date(explicitDueDate) });
          return;
        }
        const dueDay = override?.due_day || item.dueDay || item.due_day;
        if (dueDay) {
          const calculated = calculateDueDate(weekNum, dueDay);
          if (calculated) {
            items.push({ id: item.id, title: item.title, weekNumber: weekNum, dueDate: calculated });
          }
        }
      });
    });
    return items;
  }, [modules, cohort, cohortItemOverrides, selectedCohortId]);

  const eligibilityMap = useMemo(() => {
    const map = {};
    const { sessions, records, submissions } = eligibilityData;

    const recordLookup = {};
    records.forEach(r => { recordLookup[`${r.session_id}-${r.student_id}`] = r.status; });

    const subLookup = {};
    submissions.forEach(s => { subLookup[`${s.student_id}-${s.module_item_id}`] = s; });

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    students.forEach(student => {
      const reasons = [];
      let isEligible = true;

      sessions.forEach(session => {
        if (session.session_date) {
          const sessionDate = new Date(session.session_date + 'T23:59:59');
          if (sessionDate > today) return;
        }
        const status = recordLookup[`${session.id}-${student.id}`];
        if (status === 'other') return;
        if (status === 'present' || status === 'excused') return;

        isEligible = false;
        const dateStr = session.session_date
          ? new Date(session.session_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
          : 'unknown date';
        if (status === 'late') {
          reasons.push({ type: 'attendance', text: `Late to class on ${dateStr}` });
        } else if (status === 'left-early') {
          reasons.push({ type: 'attendance', text: `Left early on ${dateStr}` });
        } else {
          reasons.push({ type: 'attendance', text: `Absent on ${dateStr}` });
        }
      });

      assignmentsWithDueDates.forEach(assignment => {
        if (assignment.dueDate && assignment.dueDate > today) return;
        const sub = subLookup[`${student.id}-${assignment.id}`];
        if (!sub || sub.status === 'draft') {
          isEligible = false;
          reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' not submitted` });
          return;
        }
        if (sub.is_late) {
          isEligible = false;
          reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' submitted late` });
          return;
        }
        if (sub.submitted_at && assignment.dueDate) {
          if (new Date(sub.submitted_at) > assignment.dueDate) {
            isEligible = false;
            reasons.push({ type: 'assignment', text: `Assignment '${assignment.title}' submitted late` });
          }
        }
      });

      map[student.id] = { isEligible, reasons };
    });
    return map;
  }, [students, eligibilityData, assignmentsWithDueDates]);

  const handleEligibilityClick = (student) => {
    setEligibilityModalStudent(student);
    setShowEligibilityModal(true);
  };

  // Get completion status color
  const getProgressColor = (percentage) => {
    if (percentage === 100) return 'var(--success)';
    if (percentage >= 75) return '#66BB6A';
    if (percentage >= 50) return '#FFA726';
    if (percentage >= 25) return '#FF7043';
    return 'var(--gray-400)';
  };

  return (
    <div className="student-progress-page">
      <div className="page-header">
        <div>
          <h1>📊 Student Progress</h1>
          <p>Track student completion across modules</p>
        </div>
      </div>

      {/* Cohort Selector */}
      <div className="cohort-selector-card">
        <label>Select Cohort:</label>
        <select 
          value={selectedCohortId || ''} 
          onChange={(e) => setSelectedCohortId(e.target.value || null)}
        >
          <option value="">Choose a cohort...</option>
          {cohorts.map(cohort => (
            <option key={cohort.id} value={cohort.id}>
              {cohort.name} ({cohort.status})
            </option>
          ))}
        </select>
      </div>

      {!selectedCohortId && (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>Select a Cohort</h3>
          <p>Choose a cohort above to view student progress</p>
        </div>
      )}

      {selectedCohortId && isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading student progress...</p>
        </div>
      )}

      {selectedCohortId && !isLoading && students.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No Students Found</h3>
          <p>No students are enrolled in this cohort yet</p>
        </div>
      )}

      {selectedCohortId && !isLoading && students.length > 0 && (
        <>
          {/* Summary Stats */}
          <div className="progress-summary-cards">
            <div className="summary-card">
              <div className="summary-icon">👥</div>
              <div className="summary-content">
                <div className="summary-value">{students.length}</div>
                <div className="summary-label">Total Students</div>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">✅</div>
              <div className="summary-content">
                <div className="summary-value">
                  {students.filter(s => getStudentStats(s.id).overallPercentage === 100).length}
                </div>
                <div className="summary-label">Fully Complete</div>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">📈</div>
              <div className="summary-content">
                <div className="summary-value">
                  {Math.round(
                    students.reduce((acc, s) => acc + getStudentStats(s.id).overallPercentage, 0) / students.length
                  )}%
                </div>
                <div className="summary-label">Avg. Completion</div>
              </div>
            </div>
            
            <div className="summary-card">
              <div className="summary-icon">⚠️</div>
              <div className="summary-content">
                <div className="summary-value">
                  {students.filter(s => getStudentStats(s.id).overallPercentage < 25).length}
                </div>
                <div className="summary-label">Need Attention</div>
              </div>
            </div>
          </div>

          {/* Eligibility Tracker */}
          <div style={{ marginBottom: '24px', backgroundColor: 'var(--card-bg, #fff)', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <EligibilityTracker cohortId={selectedCohortId} />
          </div>

          {/* Module Headers */}
          <div className="progress-table-container">
            <table className="progress-table">
              <thead>
                <tr>
                  <th className="student-col">Student</th>
                  <th className="overall-col">Overall</th>
                  <th className="percentile-col">Eligibility</th>
                  <th className="percentile-col">UWorld %</th>
                  <th className="percentile-col">Attendance</th>
                  {modules.map(module => (
                    <th key={module.id} className="module-col">
                      W{module.weekNumber || module.week_number}
                    </th>
                  ))}
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const stats = getStudentStats(student.id);
                  const isExpanded = expandedStudents[student.id];

                  return (
                    <React.Fragment key={student.id}>
                      <tr className={`student-row ${isExpanded ? 'expanded' : ''}`}>
                        <td className="student-col">
                          <div className="student-info">
                            {student.avatar_url ? (
                              <img src={student.avatar_url} alt={student.name} className="student-avatar-img" />
                            ) : (
                              <div className="student-avatar">
                                {(student.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()}
                              </div>
                            )}
                            <div className="student-details">
                              <div className="student-name">{student.name}</div>
                              <div className="student-email">{student.email}</div>
                              {student.nclex_test_date && (
                                <div className="student-email" style={{ fontSize: '11px', color: '#f59e0b' }}>
                                  📋 NCLEX: {new Date(student.nclex_test_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="overall-col">
                          <div className="progress-cell">
                            <div className="progress-bar-mini">
                              <div 
                                className="progress-fill-mini"
                                style={{ 
                                  width: `${stats.overallPercentage}%`,
                                  backgroundColor: getProgressColor(stats.overallPercentage)
                                }}
                              ></div>
                            </div>
                            <span className="progress-text">{stats.overallPercentage}%</span>
                          </div>
                        </td>
                        <td className="percentile-col">
                          {(() => {
                            const elig = eligibilityMap[student.id] || { isEligible: true, reasons: [] };
                            return elig.isEligible ? (
                              <span style={{
                                padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                backgroundColor: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0'
                              }}>
                                Eligible
                              </span>
                            ) : (
                              <span
                                onClick={() => handleEligibilityClick(student)}
                                style={{
                                  padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                  backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
                                  cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted'
                                }}
                                title={`${elig.reasons.length} issue${elig.reasons.length !== 1 ? 's' : ''} — click for details`}
                              >
                                Ineligible
                              </span>
                            );
                          })()}
                        </td>
                        <td className="percentile-col">
                          {(() => {
                            const latestPercentile = getLatestPercentile(student.id);
                            if (!latestPercentile) {
                              return <span className="no-percentile">--</span>;
                            }
                            const color = latestPercentile.percentile >= 60 ? 'var(--success)' : 
                                          latestPercentile.percentile >= 40 ? '#FFA726' : 'var(--error)';
                            return (
                              <div className="percentile-badge" style={{ color }}>
                                <span className="percentile-value">{latestPercentile.percentile}%</span>
                                <span className="percentile-week">W{latestPercentile.week_number}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="percentile-col">
                          {(() => {
                            const attendance = getAttendanceRate(student.id);
                            if (!attendance) {
                              return <span className="no-percentile">--</span>;
                            }
                            const color = attendance.rate >= 90 ? 'var(--success)' : 
                                          attendance.rate >= 75 ? '#FFA726' : 'var(--error)';
                            return (
                              <div className="percentile-badge" style={{ color }} title={`${attendance.present} present, ${attendance.late} late, ${attendance.excused} excused, ${attendance['left-early'] || 0} left early, ${attendance.other || 0} other, ${attendance.absent} absent out of ${attendance.total} sessions`}>
                                <span className="percentile-value">{attendance.rate}%</span>
                                <span className="percentile-week">{attendance.present + attendance.late}/{attendance.total}</span>
                              </div>
                            );
                          })()}
                        </td>
                        {modules.map(module => {
                          const weekNum = module.weekNumber || module.week_number;
                          const moduleStat = stats.moduleStats[weekNum] || { completed: 0, total: 0, percentage: 0 };
                          
                          return (
                            <td key={module.id} className="module-col">
                              <div 
                                className={`module-progress-badge ${moduleStat.percentage === 100 ? 'complete' : ''}`}
                                title={`${moduleStat.completed}/${moduleStat.total} items`}
                              >
                                {moduleStat.percentage === 100 ? (
                                  <span className="check-icon">✓</span>
                                ) : (
                                  <span>{moduleStat.percentage}%</span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="actions-col">
                          <button 
                            className="btn-icon"
                            onClick={() => toggleStudent(student.id)}
                            title="Toggle details"
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          <button 
                            className="btn-icon"
                            onClick={() => openNotesPanel(student)}
                            title="Notes & Percentiles"
                          >
                            📝
                          </button>
                          <button 
                            className="btn-icon"
                            onClick={() => openStudentDetail(student)}
                            title="View full details"
                          >
                            📋
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Row - Module Details */}
                      {isExpanded && (
                        <tr className="expanded-row">
                          <td colSpan={modules.length + 6}>
                            <div className="expanded-content">
                              {/* Legend for icons */}
                              <div className="progress-legend">
                                <span className="legend-title">Legend:</span>
                                <div className="legend-item">
                                  <span className="legend-icon viewed">👁️</span>
                                  <span>Clicked Into / Viewed</span>
                                </div>
                                <div className="legend-item">
                                  <span className="legend-icon complete">✓</span>
                                  <span>Marked Complete by Student</span>
                                </div>
                                <div className="legend-item">
                                  <span className="legend-icon empty">○</span>
                                  <span>Not Yet</span>
                                </div>
                              </div>
                              
                              {modules.map(module => {
                                const weekNum = module.weekNumber || module.week_number;
                                const items = module.items || [];
                                const studentProgress = studentsProgress[student.id] || {};
                                
                                return (
                                  <div key={module.id} className="module-detail-section">
                                    <h4>Week {weekNum}: {module.title}</h4>
                                    {/* Column headers */}
                                    <div className="checklist-header">
                                      <span className="header-col viewed">Viewed</span>
                                      <span className="header-col complete">Done</span>
                                      <span className="header-col title">Item</span>
                                      <span className="header-col type">Type</span>
                                      <span className="header-col dates">Timestamps</span>
                                    </div>
                                    <div className="items-checklist">
                                      {items.map(item => {
                                        const progress = studentProgress[item.id] || {};
                                        const isViewed = progress.viewed_at != null;
                                        const isComplete = progress.completed === true;
                                        const viewedAt = progress.viewed_at;
                                        const completedAt = progress.completed_at;
                                        
                                        return (
                                          <div 
                                            key={item.id} 
                                            className={`checklist-item ${isComplete ? 'complete' : isViewed ? 'viewed' : 'incomplete'}`}
                                          >
                                            <div className="check-boxes">
                                              <span className={`check-box viewed ${isViewed ? 'yes' : 'no'}`} title={isViewed ? `Viewed: ${new Date(viewedAt).toLocaleString()}` : 'Not viewed yet'}>
                                                {isViewed ? '👁️' : '○'}
                                              </span>
                                              <span className={`check-box completed ${isComplete ? 'yes' : 'no'}`} title={isComplete ? `Completed: ${new Date(completedAt).toLocaleString()}` : 'Not marked complete'}>
                                                {isComplete ? '✓' : '○'}
                                              </span>
                                            </div>
                                            <span className="item-title">{item.title}</span>
                                            <span className="item-type">{item.type}</span>
                                            <div className="item-dates">
                                              {isViewed && viewedAt && (
                                                <span className="viewed-date">
                                                  Viewed: {new Date(viewedAt).toLocaleDateString()}
                                                </span>
                                              )}
                                              {isComplete && completedAt && (
                                                <span className="completed-date">
                                                  Done: {new Date(completedAt).toLocaleDateString()}
                                                </span>
                                              )}
                                              {!isViewed && !isComplete && (
                                                <span className="no-activity">No activity</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <div className="modal-overlay" onClick={() => setSelectedStudent(null)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 {selectedStudent.name}'s Progress</h3>
              <button className="modal-close" onClick={() => setSelectedStudent(null)}>×</button>
            </div>
            
            <div className="student-detail-content">
              {(() => {
                const stats = getStudentStats(selectedStudent.id);
                const studentProgress = studentsProgress[selectedStudent.id] || {};
                
                return (
                  <>
                    {/* Overall Progress */}
                    <div className="detail-overall">
                      <div className="overall-progress-ring">
                        <svg viewBox="0 0 100 100">
                          <circle 
                            cx="50" cy="50" r="45" 
                            fill="none" 
                            stroke="var(--gray-200)" 
                            strokeWidth="8"
                          />
                          <circle 
                            cx="50" cy="50" r="45" 
                            fill="none" 
                            stroke={getProgressColor(stats.overallPercentage)}
                            strokeWidth="8"
                            strokeDasharray={`${stats.overallPercentage * 2.83} 283`}
                            strokeLinecap="round"
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="ring-text">
                          <span className="ring-percentage">{stats.overallPercentage}%</span>
                          <span className="ring-label">Complete</span>
                        </div>
                      </div>
                      <div className="overall-stats">
                        <div className="stat-row">
                          <span>Items Completed:</span>
                          <strong>{stats.completedItems} / {stats.totalItems}</strong>
                        </div>
                        <div className="stat-row">
                          <span>Email:</span>
                          <strong>{selectedStudent.email}</strong>
                        </div>
                        <div className="stat-row">
                          <span>Status:</span>
                          <strong className={`status-${selectedStudent.status}`}>
                            {selectedStudent.status}
                          </strong>
                        </div>
                      </div>
                    </div>

                    {/* Attendance Summary */}
                    {(() => {
                      const attendance = getAttendanceRate(selectedStudent.id);
                      if (!attendance) return null;
                      
                      return (
                        <div className="detail-attendance-summary">
                          <h4>📇 Attendance</h4>
                          <div className="attendance-detail-grid">
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: 'var(--success)' }}>{attendance.present}</span>
                              <span className="attendance-detail-label">Present</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: '#FFA726' }}>{attendance.late}</span>
                              <span className="attendance-detail-label">Late</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: 'var(--blue-primary)' }}>{attendance.excused}</span>
                              <span className="attendance-detail-label">Excused</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: '#ab47bc' }}>{attendance['left-early'] || 0}</span>
                              <span className="attendance-detail-label">Left Early</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: '#78909c' }}>{attendance.other || 0}</span>
                              <span className="attendance-detail-label">Other</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: 'var(--error)' }}>{attendance.absent}</span>
                              <span className="attendance-detail-label">Absent</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value">{attendance.total}</span>
                              <span className="attendance-detail-label">Total Sessions</span>
                            </div>
                            <div className="attendance-detail-stat">
                              <span className="attendance-detail-value" style={{ color: attendance.rate >= 90 ? 'var(--success)' : attendance.rate >= 75 ? '#FFA726' : 'var(--error)' }}>
                                {attendance.rate}%
                              </span>
                              <span className="attendance-detail-label">Attendance Rate</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Module Breakdown */}
                    <div className="detail-modules">
                      {modules.map(module => {
                        const weekNum = module.weekNumber || module.week_number;
                        const items = module.items || [];
                        const moduleStat = stats.moduleStats[weekNum] || { completed: 0, total: 0, percentage: 0 };
                        
                        return (
                          <div key={module.id} className="detail-module-card">
                            <div className="module-card-header">
                              <div className="module-card-title">
                                <span className="week-badge">Week {weekNum}</span>
                                <h4>{module.title}</h4>
                              </div>
                              <div className={`module-card-progress ${moduleStat.percentage === 100 ? 'complete' : ''}`}>
                                {moduleStat.percentage === 100 ? '✓' : `${moduleStat.percentage}%`}
                              </div>
                            </div>
                            
                            <div className="module-card-items">
                              {items.map(item => {
                                const isComplete = studentProgress[item.id]?.completed === true;
                                
                                return (
                                  <div 
                                    key={item.id}
                                    className={`detail-item ${isComplete ? 'complete' : ''}`}
                                  >
                                    <span className="detail-item-check">
                                      {isComplete ? '✓' : '○'}
                                    </span>
                                    <span className="detail-item-title">{item.title}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Eligibility Reasons Modal */}
      {showEligibilityModal && eligibilityModalStudent && (
        <div className="modal-overlay" onClick={() => setShowEligibilityModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3>Eligibility Details — {eligibilityModalStudent.name}</h3>
              <button className="modal-close" onClick={() => setShowEligibilityModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {(() => {
                const elig = eligibilityMap[eligibilityModalStudent.id];
                if (!elig || elig.reasons.length === 0) {
                  return <p style={{ color: '#6b7280' }}>No issues found. This student is eligible.</p>;
                }

                const attendanceReasons = elig.reasons.filter(r => r.type === 'attendance');
                const assignmentReasons = elig.reasons.filter(r => r.type === 'assignment');

                return (
                  <>
                    {attendanceReasons.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#374151' }}>
                          Attendance Issues ({attendanceReasons.length})
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'none' }}>
                          {attendanceReasons.map((r, i) => (
                            <li key={i} style={{
                              padding: '6px 0', borderBottom: '1px solid #f3f4f6',
                              fontSize: '13px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                              <span style={{ fontSize: '10px' }}>&#9679;</span> {r.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {assignmentReasons.length > 0 && (
                      <div>
                        <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#374151' }}>
                          Assignment Issues ({assignmentReasons.length})
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px', listStyle: 'none' }}>
                          {assignmentReasons.map((r, i) => (
                            <li key={i} style={{
                              padding: '6px 0', borderBottom: '1px solid #f3f4f6',
                              fontSize: '13px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                              <span style={{ fontSize: '10px' }}>&#9679;</span> {r.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Student Notes Panel */}
      <StudentNotesPanel 
        student={notesStudent}
        isOpen={showNotesPanel}
        onClose={() => {
          setShowNotesPanel(false);
          setNotesStudent(null);
        }}
      />
    </div>
  );
};

export default StudentProgress;
